import "./tailwind.css";

import { Document, NotFound404Error } from "./common/components";
import { Header, Modals, RegionSelector } from "./components";
import type {
  HeadersFunction,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { Suspense, lazy } from "react";
import { crateCanonicalLink, getFallbackMeta } from "./utils";
import { json, redirect } from "@remix-run/node";

import { AnimatePresence } from "framer-motion";
import { Footer } from "~/components";
import GoogleTagManagerScript from "./ThirdPartyScripts/GoogleTagManagerScript";
import KlaviyoScript from "./ThirdPartyScripts/KlaviyoScript";
import SanityLiveMode from "./components/SanityLiveMode";
import { SpeedInsights } from "@vercel/speed-insights/remix";
import { ThirdPartyScripts } from "./common/components/Document";
import { Toaster } from "~/components/ui/toaster";
import { loadQueryOptions } from "./sanity/loadQueryOptions.server";
import styles from "~/tailwind.css?url";

//import styles from "~/tailwind.css?url";
// import styles from "./tailwind.css";

const ExitPreview = lazy(() => import("~/components/ExitPreview"));
const EnterPreview = lazy(() => import("~/components/EnterPreview"));

export type RootLoader = typeof loader;

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    "Cache-Control": loaderHeaders.get("Cache-Control") ?? "no-store",
  };
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  return [
    crateCanonicalLink(location.pathname),
    ...getFallbackMeta(data?.market?.locale),
  ];
};

export const links: LinksFunction = () => {
  return [
    { rel: "stylesheet", href: styles },
    { rel: "preconnect", href: "https://cdn.sanity.io" },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: `/icons/favicon-32x32.png`,
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: `/icons/favicon-16x16.png`,
    },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: `/icons/apple-touch-icon.png`,
    },
  ];
};

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const { preview, options, market } = await loadQueryOptions(
    request.headers,
    request.url
  );

  const url = new URL(request.url);
  const isStudioUrl = url.pathname.includes("studio/");

  // Only perform redirect on the root URL "/"
  if (url.pathname === "/") {
    // Vercel provides a special header for geo-location
    const country = request.headers.get("x-vercel-ip-country")?.toLowerCase();

    // Redirect based on country code
    if (country === "se") {
      return redirect("/se");
    } else if (country === "us") {
      return redirect("/us");
    }

    return redirect("/se");
  }

  if (!market && !isStudioUrl) {
    throw json(
      {
        marketIsUnsupported: true,
      },
      { status: 404 }
    );
  }

  return json({
    isStudioUrl,
    market,
    sanity: { preview: preview },
    isDev: process.env.NODE_ENV === "development",
    ENV: {
      VITE_SANITY_PROJECT_ID: import.meta.env.VITE_SANITY_PROJECT_ID!,
      VITE_SANITY_DATASET: import.meta.env.VITE_SANITY_DATASET!,
      VITE_SANITY_API_VERSION: import.meta.env.VITE_SANITY_API_VERSION!,
    },
  });
};

export default function App() {
  const { market, ENV, isStudioUrl, sanity, isDev } =
    useLoaderData<typeof loader>();

  return (
    <html dir="ltr" lang={market?.languageCode || "en"}>
      <head>
        <meta charSet="utf-8" />
        <Meta />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Links />

        <ThirdPartyScripts>
          <GoogleTagManagerScript />
          <KlaviyoScript />
        </ThirdPartyScripts>
      </head>
      <body>
        {isStudioUrl ? null : <Header locale={market?.locale} />}

        {isStudioUrl ? (
          <Outlet />
        ) : (
          <main>
            <AnimatePresence mode="wait">
              <Outlet />
            </AnimatePresence>
          </main>
        )}

        <Toaster />

        {isStudioUrl ? null : <Footer locale={market?.locale ?? "se"} />}

        <Modals
          locale={market?.locale}
          currencySymbol={market?.currencySymbol}
        />

        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`,
          }}
        />
        {isDev && !isStudioUrl && sanity.preview && (
          <Suspense>
            <SanityLiveMode />
            <ExitPreview />
          </Suspense>
        )}

        {isDev && !isStudioUrl && !sanity.preview && (
          <Suspense>
            <EnterPreview />
          </Suspense>
        )}
        <SpeedInsights />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError() as {
    status: number;
    data: {
      title: string;
      message: string;
      linkLabel: string;
      market: { languageCode: string; locale: string };
      marketIsUnsupported: boolean;
    };
  };

  if (error?.data?.marketIsUnsupported) {
    return (
      <Document languageCode="en">
        <main>
          <Header locale="us" />

          <RegionSelector />
        </main>
      </Document>
    );
  }

  return (
    <Document languageCode={error?.data?.market?.languageCode ?? "en"}>
      <main>
        {error?.status === 404 ? (
          <>
            <Header locale={error?.data?.market?.locale} />
            <NotFound404Error
              locale={error?.data?.market?.locale}
              title={error?.data?.title}
              message={error?.data?.message}
              linkLabel={error?.data?.linkLabel}
            />
            <Footer locale={error?.data?.market.locale} />
          </>
        ) : (
          <div>
            {
              // Server error
            }
            <>
              <Header locale="us" />

              <NotFound404Error
                locale="us"
                title={`It's noy you, it's us`}
                message={
                  "We are experiencing technical difficulties. Please try again later."
                }
                linkLabel={"Go back to the homepage"}
              />
            </>
          </div>
        )}
      </main>
    </Document>
  );
};
